import { getPatientFile } from '../endpoints';
import { WsError } from '@/lib/wiservice';
import { flattenMilestones } from '@/logic/carePeriod';

// apiv2 WS_STATUS_OBJECT_NOTFOUND — get_patient_file returns 200-with-nulls for a
// missing patient (not 404), so this is defensive only.
const WS_STATUS_OBJECT_NOTFOUND = 380;

export interface PatientFile {
  carePeriod: unknown;
  carePlan: unknown;
  measureStates: unknown[];
  tasks: unknown[];
  insurance: unknown;
  consent: unknown;
  intakes: unknown[];
  intakesTotalCount: number;
  eligibility: {
    checkedAt: string | null;
    summary: unknown;
    taskStatus: string | null;
    taskCompletedAt: string | null;
    claims: { diagnoses: unknown[]; medications: unknown[]; labs: unknown[] };
  };
  educationDigest: unknown;
}

function pickIntakesTotalCount(data: Record<string, unknown>): number {
  if (Number.isFinite(data.intakesTotalCount as number)) return data.intakesTotalCount as number;
  return Array.isArray(data.intakes) ? data.intakes.length : 0;
}

// NOT_FOUND → null (documented). Other errors propagate.
export async function fetchPatientFile(
  withingsUserId: string | number,
  tasksLimit?: number,
): Promise<PatientFile | null> {
  let data: Record<string, unknown> | undefined;
  try {
    // Native apiv2 `clinical_view.get_patient_file` (session-authed via ws()).
    // Returns { data: {carePeriod,carePlan,…} } — same shape the bridge unwrapped.
    const params: Record<string, unknown> = { clinical_id_dev_user: String(withingsUserId) };
    if (Number.isFinite(tasksLimit as number)) params.clinical_tasks_limit = tasksLimit;
    const body = await getPatientFile(params);
    data = (body?.data as Record<string, unknown>) || undefined;
  } catch (e) {
    if ((e as WsError)?.status === WS_STATUS_OBJECT_NOTFOUND) return null;
    throw e;
  }
  if (!data) return null;
  return {
    carePeriod: data.carePeriod ? flattenMilestones(data.carePeriod as Record<string, unknown>) : null,
    carePlan: data.carePlan || null,
    measureStates: Array.isArray(data.measureStates) ? data.measureStates : [],
    tasks: Array.isArray(data.tasks) ? data.tasks : [],
    insurance: data.insurance || null,
    consent: data.consent || null,
    intakes: Array.isArray(data.intakes) ? data.intakes : [],
    intakesTotalCount: pickIntakesTotalCount(data),
    eligibility: (data.eligibility as PatientFile['eligibility']) || {
      checkedAt: null,
      summary: null,
      taskStatus: null,
      taskCompletedAt: null,
      claims: { diagnoses: [], medications: [], labs: [] },
    },
    educationDigest: data.educationDigest || null,
  };
}
